import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit';
import { RootState } from 'store';
import { loadLocalStorage, saveLocalStorage } from 'utils';
import { auth, LoginProps, LOGIN_STORAGE_KEY } from 'utils/auth';
import { segmentsApi } from './services/segmentsApi';
import { StoreInfo } from './services/types';

export enum SubscriptionLevel {
  free,
  small_business,
  growth,
  unlimited,
  enterprise,
}

interface UserDetailsState {
  loading: boolean;
  error: SerializedError | null;
  loggedIn: boolean;
  loggedInUser: string | null;
  avatar_url: string | null;
  stores: Partial<StoreInfo>[] | null;
  token: string | null;
  role_id: number | null;
  currentStore: {
    completeInfo: Partial<StoreInfo> | null | undefined;
    subscriptionPlan: {
      level: SubscriptionLevel;
      type: 'regular' | 'trial' | 'partner' | undefined;
    } | null;
    serializedStoreName: string;
  };
}

const initialState: UserDetailsState = {
  loading: false,
  error: null,
  loggedIn: false,
  loggedInUser: null,
  avatar_url: null,
  stores: null,
  token: null,
  role_id: null,
  currentStore: {
    completeInfo: null,
    subscriptionPlan: null,
    serializedStoreName: '',
  },
};

// Common Reducer methods
type StartLoading = (state: UserDetailsState) => UserDetailsState;
const startLoading: StartLoading = (state) => ({
  ...state,
  loading: true,
  error: null,
});

type ReceiveError = (
  state: UserDetailsState,
  action: { error: SerializedError }
) => UserDetailsState;
const receiveError: ReceiveError = (state, action) => ({
  ...state,
  loading: false,
  error: action.error,
});

// Async Actions
export const login = createAsyncThunk('signin/login', auth.login);
export const register = createAsyncThunk('signin/register', auth.register);
export const loginWithShopifyCredentials = createAsyncThunk(
  'signin/loginWithShopifyCredentials',
  auth.loginWithShopifyCredentials
);
export const registerWithShopifyCredentials = createAsyncThunk(
  'signin/registerWithShopifyCredentials',
  auth.registerWithShopifyCredentials
);

// Main Slice/Reducer
const userDetailsSlice = createSlice({
  name: 'userDetails',
  initialState,
  reducers: {
    selectStore(
      state,
      { payload }: PayloadAction<{ storeId?: number; store?: StoreInfo }>
    ) {
      let storeInfo;
      if (payload.store) {
        storeInfo = payload.store;
      } else {
        storeInfo = state.stores?.find((store) => store.id === payload.storeId);
      }

      if (storeInfo?.active_sub?.level) {
        const level = storeInfo.active_sub.level
          .toLowerCase()
          .replace(' ', '_') as keyof typeof SubscriptionLevel;

        const isUserTreslAdmin = state.role_id === 1;

        const plan = isUserTreslAdmin
          ? SubscriptionLevel.enterprise
          : SubscriptionLevel[level];

        const currentStore = {
          completeInfo: storeInfo,
          serializedStoreName: `store_name=${storeInfo.store_name}`,
          subscriptionPlan: {
            level: plan as SubscriptionLevel,
            type: storeInfo.active_sub.type,
          },
        };

        state.currentStore = currentStore;

        let userLoginInfo = loadLocalStorage(LOGIN_STORAGE_KEY);

        userLoginInfo = {
          ...userLoginInfo,
          currentStore,
        };
        saveLocalStorage(LOGIN_STORAGE_KEY, userLoginInfo);
      }
    },
    setUserInfo(state, { payload }: PayloadAction<LoginProps>) {
      const isUserTreslAdmin = state.role_id === 1;

      const level = isUserTreslAdmin
        ? SubscriptionLevel.enterprise
        : payload.stores?.length
        ? SubscriptionLevel[
            payload.stores[0].active_sub?.level
              .toLowerCase()
              .replace(' ', '_') as keyof typeof SubscriptionLevel
          ]
        : SubscriptionLevel.growth;

      const currentStore = {
        completeInfo: payload.stores?.length ? payload.stores[0] : null,
        subscriptionPlan: {
          type: payload.stores?.length
            ? payload.stores[0].active_sub?.type
            : 'regular',
          level: level,
        },
        serializedStoreName: payload.stores?.length
          ? `store_name=${payload.stores[0].store_name}`
          : '',
      };

      state.error = null;
      state.loading = false;
      state.loggedIn = true;
      state.loggedInUser = payload.name;
      state.avatar_url = payload.avatar_url;
      state.token = payload.access_token;
      state.stores = payload.stores;
      state.role_id = payload.role_id;
      state.currentStore = currentStore;

      saveLocalStorage(LOGIN_STORAGE_KEY, {
        ...payload,
        currentStore: currentStore,
      });
    },
    logout(state) {
      localStorage.removeItem(LOGIN_STORAGE_KEY);
      if (window.location) {
        window.location.replace('/');
      }
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, startLoading)
      .addCase(login.rejected, receiveError)
      .addCase(login.fulfilled, (state, action) => {
        userDetailsSlice.caseReducers.setUserInfo(state, action);
      })
      .addCase(register.pending, startLoading)
      .addCase(register.rejected, receiveError)
      .addCase(register.fulfilled, (state, action) => {
        userDetailsSlice.caseReducers.setUserInfo(state, action);
      })
      .addCase(loginWithShopifyCredentials.pending, startLoading)
      .addCase(loginWithShopifyCredentials.rejected, receiveError)
      .addCase(loginWithShopifyCredentials.fulfilled, (state, action) => {
        userDetailsSlice.caseReducers.setUserInfo(state, action);
      })
      .addCase(registerWithShopifyCredentials.pending, startLoading)
      .addCase(registerWithShopifyCredentials.rejected, receiveError)
      .addCase(registerWithShopifyCredentials.fulfilled, (state, action) => {
        userDetailsSlice.caseReducers.setUserInfo(state, action);
      })
      .addMatcher(
        segmentsApi.endpoints.getStores.matchFulfilled,
        (state, { payload }) => {
          state.stores = payload;

          if (state.currentStore.completeInfo?.id) {
            const updatedCurrentStore = payload.find(
              (store) => store.id === state.currentStore.completeInfo?.id
            );

            if (updatedCurrentStore) {
              const level = updatedCurrentStore?.active_sub.level
                .toLowerCase()
                .replace(' ', '_') as keyof typeof SubscriptionLevel;

              const isUserTreslAdmin = state.role_id === 1;

              const plan = isUserTreslAdmin
                ? SubscriptionLevel.enterprise
                : SubscriptionLevel[level];

              const currentStore = {
                completeInfo: updatedCurrentStore,
                serializedStoreName: `store_name=${updatedCurrentStore?.store_name}`,
                subscriptionPlan: {
                  level: plan as SubscriptionLevel,
                  type: updatedCurrentStore?.active_sub.type,
                },
              };

              state.currentStore = currentStore;
            }
          }

          let userLoginInfo = loadLocalStorage(LOGIN_STORAGE_KEY);

          userLoginInfo = {
            ...userLoginInfo,
            payload,
          };
          saveLocalStorage(LOGIN_STORAGE_KEY, userLoginInfo);
        }
      );
  },
});

export const userDetailsState = (state: RootState) => state.userDetails;

export default userDetailsSlice.reducer;

export const { selectStore, logout } = userDetailsSlice.actions;
